import React, { useEffect, useRef, useState } from 'react';
import { HiOutlineLockClosed } from 'react-icons/hi';
import {
    SYSTEM_CONSTANTS,
    SYSTEM_CONSTANT_SCOPE_LABELS,
    SystemConstantScope,
} from '../../../../../utils/communicationConstants';

interface ConstantPickerProps {
    onPick: (name: string) => void;
    compact?: boolean;
    align?: 'left' | 'right';
}

const ConstantPicker: React.FC<ConstantPickerProps> = ({ onPick, compact, align = 'right' }) => {
    const [open, setOpen] = useState(false);
    const rootRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (!open) return undefined;
        const close = (event: MouseEvent) => {
            if (rootRef.current && !rootRef.current.contains(event.target as Node)) setOpen(false);
        };
        document.addEventListener('mousedown', close);
        return () => document.removeEventListener('mousedown', close);
    }, [open]);

    return (
        <div ref={rootRef} className="relative inline-block">
            <button
                type="button"
                onClick={() => setOpen((o) => !o)}
                className={`inline-flex items-center gap-1 rounded-md border border-dashed border-violet-400 text-violet-700 dark:text-violet-300 hover:bg-violet-50 dark:hover:bg-violet-900/30 ${
                    compact ? 'px-1.5 py-0.5 text-[11px]' : 'px-2 py-1 text-xs'
                }`}
                title="Inserir constante do sistema"
            >
                <HiOutlineLockClosed size={compact ? 12 : 14} />
                {!compact && 'Constante'}
            </button>
            {open && (
                <div
                    className={`absolute z-30 mt-1 w-80 rounded-lg border border-gray-200 dark:border-slate-600 bg-white dark:bg-slate-800 shadow-xl ${
                        align === 'right' ? 'right-0' : 'left-0'
                    }`}
                >
                    <div className="px-3 pt-2.5 pb-1.5 text-[11px] text-gray-400 border-b border-gray-100 dark:border-slate-700">
                        Preenchidas pelo sistema: do ambiente (fixas no deploy) ou do envio (calculadas na hora). Não viram campo de formulário.
                    </div>
                    <ul className="py-1 max-h-96 overflow-y-auto">
                        {(['ENVIRONMENT', 'SEND'] as SystemConstantScope[]).map((scope) => (
                            <React.Fragment key={scope}>
                                <li className="px-3 pt-2 pb-1 text-[10px] uppercase tracking-wide text-gray-400">
                                    {SYSTEM_CONSTANT_SCOPE_LABELS[scope]}
                                </li>
                                {SYSTEM_CONSTANTS.filter((c) => c.scope === scope).map((c) => (
                            <li key={c.name}>
                                <button
                                    type="button"
                                    onClick={() => {
                                        onPick(c.name);
                                        setOpen(false);
                                    }}
                                    className="w-full text-left px-3 py-2 hover:bg-slate-50 dark:hover:bg-slate-700"
                                >
                                    <span className="block text-sm text-gray-800 dark:text-gray-100">{c.label}</span>
                                    <span className="block text-[11px] font-mono text-violet-600 dark:text-violet-300">{`{{${c.name}}}`}</span>
                                    <span className="block text-[11px] text-gray-400 mt-0.5">{c.description}</span>
                                    <span className="block text-[11px] text-gray-400 mt-0.5">
                                        {c.scope === 'ENVIRONMENT' ? 'Produção' : 'Exemplo'}:{' '}
                                        <span className="font-mono">{c.valueByEnvironment.prd}</span>
                                    </span>
                                </button>
                            </li>
                                ))}
                            </React.Fragment>
                        ))}
                    </ul>
                </div>
            )}
        </div>
    );
};

export default ConstantPicker;
